import {checkParentDetermination, updateContextElements} from '../Form/CheckboxGroup';
import {Modal} from '../Modal';
import {showRequestErrorToast} from '../RequestError';
import {translate} from '../../Library/Translator';
import {isTasksCompact} from '../../Library/Viewport';

const AUTO_REFRESH_STORAGE_KEY = 'tasksAutoRefresh';
const POLL_HEARTBEAT_MILLISECONDS = 2_000;
const POLL_QUIET_GRACE = 4;
const POLL_MAX_DELAY_MILLISECONDS = 30_000;
const POLL_BASE_DELAYS = {
    'tasks-stat-strip': 2_000,
    'admin-stats': 2_000,
    'tasks-tab-content': 2_000,
    'admin-task-list': 2_000,
    'admin-open-group': 2_000,
};

const STAT_STRIP_IDS = ['tasks-stat-strip', 'admin-stats'];
const STAT_REGION_IDS = [...STAT_STRIP_IDS, 'admin-webhook-stats'];
const LIST_REGION_IDS = ['tasks-tab-content', 'admin-task-list'];
const LIST_REFRESH_EVENTS = {
    'tasks-tab-content': 'tasksrefresh',
    'admin-task-list': 'activelistpoll',
};

const pollState = new Map();

let pollTimer = null;
let autoRefreshEnabled = false;
let expandedGroupKeys = null;

const firstPresent = (ids) => ids.map(id => document.getElementById(id)).find(Boolean) ?? null;

const statStripRegion = () => firstPresent(STAT_STRIP_IDS);

const contentRegion = () => firstPresent(LIST_REGION_IDS);

const userIsBusy = () => {
    const region = contentRegion();

    if (!region) {
        return false;
    }

    if (region.querySelector('input[name="taskIds[]"]:checked')) {
        return true;
    }

    if (region.querySelector('.group-pager-jump input:not([hidden])')) {
        return true;
    }

    return Array.from(region.querySelectorAll('.pager-number.active'))
        .some(pageButton => pageButton.textContent.trim() !== '1');
};

const pollStateFor = (targetId) => {
    if (!pollState.has(targetId)) {
        pollState.set(targetId, {streak: 0, nextDueAt: 0});
    }

    return pollState.get(targetId);
};

const pollDelay = (targetId) => Math.min(
    POLL_BASE_DELAYS[targetId] * 2 ** Math.max(0, pollStateFor(targetId).streak - POLL_QUIET_GRACE),
    POLL_MAX_DELAY_MILLISECONDS,
);

const schedulePoll = (targetId) => {
    pollStateFor(targetId).nextDueAt = Date.now() + pollDelay(targetId);
};

const pollIsDue = (targetId) => pollStateFor(targetId).nextDueAt <= Date.now();

export const resetTaskPollBackoff = () => {
    if (!statStripRegion() && !contentRegion()) {
        return;
    }

    pollState.forEach(state => {
        state.streak = 0;
        state.nextDueAt = 0;
    });
};

const pollTick = () => {
    if (document.hidden || isTasksCompact()) {
        return;
    }

    const strip = statStripRegion();

    if (strip && pollIsDue(strip.id)) {
        schedulePoll(strip.id);
        strip.dispatchEvent(new CustomEvent('statsrefresh'));
    }

    const region = contentRegion();

    if (!autoRefreshEnabled || !region || !pollIsDue(region.id) || userIsBusy()) {
        return;
    }

    schedulePoll(region.id);

    const openGroup = document.querySelector('[data-admin-group].is-open');

    if (openGroup) {
        if (pollIsDue('admin-open-group')) {
            schedulePoll('admin-open-group');
            openGroup.dispatchEvent(new CustomEvent('admingrouprefresh'));
        }

        return;
    }

    expandedGroupKeys = Array.from(region.querySelectorAll('[data-task-group]:not(.collapsed)'))
        .map(group => group.dataset.groupKey);

    region.dispatchEvent(new CustomEvent(LIST_REFRESH_EVENTS[region.id]));
};

const NUMBER_ANIMATION_DURATION_MILLISECONDS = 400;

const lastSwapResponses = new Map();

let pendingStatCounts = null;
let pendingStripId = null;

const statTileKey = (tile) => Array.from(tile.classList)
    .find(className => className.startsWith('stat-') && className !== 'stat-tile');

const parseStatNumber = (text) => Number(text.replace(/[^0-9]/g, '')) || 0;

const spinTargets = (strip) => Array.from(strip.querySelectorAll('.stat-tile .stat-number'))
    .map(element => ({element, key: `tile:${statTileKey(element.closest('.stat-tile'))}`}));

const animateNumber = (element, from, to, targetText) => {
    const start = performance.now();

    const step = (timestamp) => {
        const progress = Math.min((timestamp - start) / NUMBER_ANIMATION_DURATION_MILLISECONDS, 1);

        if (progress === 1) {
            element.textContent = targetText;
            return;
        }

        const eased = 1 - (1 - progress) ** 3;
        element.textContent = Math.round(from + (to - from) * eased).toLocaleString('en-US');

        requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
};

export const captureStatCounts = (event) => {
    const target = event.detail.target;

    if (!target || !STAT_REGION_IDS.includes(target.id) || event.detail.shouldSwap === false) {
        return;
    }

    pendingStripId = target.id;
    pendingStatCounts = new Map();

    spinTargets(target).forEach(({element, key}) => {
        pendingStatCounts.set(key, parseStatNumber(element.textContent));
    });
};

export const animateStatCounts = () => {
    if (pendingStatCounts === null) {
        return;
    }

    const previousCounts = pendingStatCounts;
    const strip = document.getElementById(pendingStripId);

    pendingStatCounts = null;
    pendingStripId = null;

    if (!strip || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }

    spinTargets(strip).forEach(({element, key}) => {
        const targetText = element.textContent.trim();
        const from = previousCounts.get(key);
        const to = parseStatNumber(targetText);

        if (from === undefined || from === to) {
            return;
        }

        animateNumber(element, from, to, targetText);
    });
};

export const skipUnchangedTasksSwap = (event) => {
    const targetId = event.detail.target?.id;

    if (POLL_BASE_DELAYS[targetId] === undefined || event.detail.shouldSwap === false) {
        return;
    }

    const response = event.detail.serverResponse ?? event.detail.xhr?.responseText;
    const state    = pollStateFor(targetId);

    if (lastSwapResponses.get(targetId) === response) {
        state.streak++;
        event.detail.shouldSwap = false;
    } else {
        state.streak = 0;
        lastSwapResponses.set(targetId, response);
    }

    schedulePoll(targetId);
};

const RUN_FLIGHT_MILLISECONDS = 320;

const markPillRunning = (pill) => {
    const spinner = document.createElement('span');
    spinner.className = 'pill-spinner';

    pill.className = 'status-pill status-running is-absorbing';
    pill.textContent = translate('page.tasks.status.Running');
    pill.prepend(spinner);
};

const flyIntoPill = (button, pill) => {
    const start = button.getBoundingClientRect();
    const target = pill.getBoundingClientRect();

    const flier = button.cloneNode(true);
    flier.classList.add('task-run-flier');
    flier.removeAttribute('data-task-run');

    flier.style.setProperty('--flight-left', `${start.left}px`);
    flier.style.setProperty('--flight-top', `${start.top}px`);
    flier.style.setProperty('--flight-width', `${start.width}px`);
    flier.style.setProperty('--flight-x', `${target.left + target.width / 2 - start.left - start.width / 2}px`);
    flier.style.setProperty('--flight-y', `${target.top + target.height / 2 - start.top - start.height / 2}px`);

    document.body.append(flier);

    requestAnimationFrame(() => flier.classList.add('is-flying'));

    setTimeout(() => {
        flier.remove();
        pill.classList.remove('is-absorbing');
    }, RUN_FLIGHT_MILLISECONDS);
};

const RUN_ERROR_TOAST_INTERVAL_MILLISECONDS = 3_000;

let lastRunErrorToastAt = null;

const reportRunFailure = () => {
    if (lastRunErrorToastAt !== null && performance.now() - lastRunErrorToastAt < RUN_ERROR_TOAST_INTERVAL_MILLISECONDS) {
        return;
    }

    lastRunErrorToastAt = performance.now();

    showRequestErrorToast();
};

const reconcileAfterRun = (outcome) => {
    if (outcome?.status === 'Success') {
        return;
    }

    reportRunFailure();
    resetTaskPollBackoff();

    const region = contentRegion();

    region?.dispatchEvent(new CustomEvent(LIST_REFRESH_EVENTS[region.id]));
};

export const runTaskNow = (button) => {
    const pill = button.closest('tr')?.querySelector('.status-pill');

    fetch(button.dataset.taskRun, {method: 'POST', keepalive: true})
        .then(response => (response.ok ? response.json() : null))
        .then(reconcileAfterRun)
        .catch(() => reconcileAfterRun(null))
    ;

    if (pill) {
        flyIntoPill(button, pill);
        markPillRunning(pill);
    }

    button.remove();
};

export const refreshStatStripOnTooltipLeave = (event) => {
    const tooltipped = event.target.closest?.('[data-tippy-content]');

    if (!tooltipped || tooltipped.contains(event.relatedTarget)) {
        return;
    }

    const strip = tooltipped.closest('.stat-strip');

    if (!strip) {
        return;
    }

    resetTaskPollBackoff();
    strip.dispatchEvent(new CustomEvent('statsrefresh'));
};

export const skipStatStripSwapWhileTooltipHovered = (event) => {
    const target = event.detail.target;

    if (!STAT_REGION_IDS.includes(target?.id)) {
        return;
    }

    if (target.querySelector('[data-tippy-content]:hover') !== null) {
        event.detail.shouldSwap = false;
    }
};

export const explainMissingTask = (event) => {
    if (event.detail.xhr?.status !== 404 || !event.detail.elt?.matches?.('.task-detail-button')) {
        return false;
    }

    const body = document.createElement('p');
    body.textContent = translate('page.tasks.detail.gone.body');

    Modal({
        title: translate('page.tasks.detail.gone.title'),
        content: body,
        cancelLabel: translate('page.tasks.detail.gone.close'),
    }).render();

    return true;
};

export const toggleTaskAutoRefresh = (checkbox) => {
    localStorage.setItem(AUTO_REFRESH_STORAGE_KEY, checkbox.checked ? 'on' : 'off');

    autoRefreshEnabled = checkbox.checked;

    if (autoRefreshEnabled) {
        resetTaskPollBackoff();
    }
};

export const initializeTaskAutoRefresh = () => {
    if (!statStripRegion() && !contentRegion()) {
        return;
    }

    const checkbox = document.querySelector('[data-tasks-auto-refresh]');

    if (checkbox && !isTasksCompact() && localStorage.getItem(AUTO_REFRESH_STORAGE_KEY) === 'on') {
        checkbox.checked = true;
        autoRefreshEnabled = true;
    }

    pollTimer ??= setInterval(pollTick, POLL_HEARTBEAT_MILLISECONDS);
};

export const restoreGroupExpansion = (region) => {
    if (expandedGroupKeys === null) {
        return;
    }

    region.querySelectorAll('[data-task-group]').forEach(group => {
        group.classList.toggle('collapsed', !expandedGroupKeys.includes(group.dataset.groupKey));
    });

    expandedGroupKeys = null;
    refreshExpandAllLabel();
};

export const refreshExpandAllLabel = () => {
    const button = document.querySelector('[data-task-groups-expand]');

    if (!button) {
        return;
    }

    const anyCollapsed = Array.from(document.querySelectorAll('[data-task-group]'))
        .some(group => group.classList.contains('collapsed'));

    button.textContent = anyCollapsed ? button.dataset.expandLabel : button.dataset.collapseLabel;
};

export const toggleTaskGroup = (toggleHandle) => {
    toggleHandle.closest('[data-task-group]').classList.toggle('collapsed');

    refreshExpandAllLabel();
};

export const toggleAllTaskGroups = (button) => {
    const groups = document.querySelectorAll('[data-task-group]');
    const anyCollapsed = Array.from(groups).some(group => group.classList.contains('collapsed'));

    groups.forEach(group => group.classList.toggle('collapsed', !anyCollapsed));

    refreshExpandAllLabel();
};

export const activateBulkSelectAll = (button) => {
    const form = button.closest('form');
    const rowCheckboxes = form.querySelectorAll('input[name="taskIds[]"]:not(:disabled)');
    const scopeInput = form.querySelector('[data-bulk-scope]');
    const selectAll = scopeInput.value !== 'group';

    if (rowCheckboxes.length === 0) {
        return;
    }

    scopeInput.value = selectAll ? 'group' : '';
    rowCheckboxes.forEach(checkbox => {
        checkbox.checked = selectAll;
    });

    button.dataset.pageScopedLabel ??= button.textContent.trim();
    button.textContent = selectAll ? button.dataset.unselectLabel : button.dataset.pageScopedLabel;
    button.classList.toggle('active', selectAll);

    checkParentDetermination(rowCheckboxes[0].dataset.checkboxGroup);
    updateContextElements(rowCheckboxes[0].dataset.checkboxGroup.split('.')[0]);

    if (selectAll) {
        form.querySelector('[data-checkbox-group-context] .count').textContent = button.dataset.groupCount;
    }
};

const destroyTooltipsWithin = (element) => {
    element._tippy?.destroy();

    element.querySelectorAll('[data-tippy-content]').forEach(tooltipped => tooltipped._tippy?.destroy());
};

const closeAdminGroup = (group) => {
    group.classList.remove('is-open');
    group.querySelectorAll('tr:not(.group-summary)').forEach(row => {
        destroyTooltipsWithin(row);
        row.remove();
    });
};

export const toggleAdminGroup = (button) => {
    const group = button.closest('[data-admin-group]');

    if (group.classList.contains('is-open')) {
        closeAdminGroup(group);

        return;
    }

    document.querySelectorAll('[data-admin-group].is-open').forEach(closeAdminGroup);

    group.classList.add('is-open');
    group.dispatchEvent(new CustomEvent('admingroupload'));
};

let resumeCountdownTimer;

export const initResumeCountdown = () => {
    clearInterval(resumeCountdownTimer);

    const element = document.querySelector('[data-resume-countdown]');

    if (!element) {
        return;
    }

    const target = parseInt(element.dataset.resumeAt, 10);
    const template = element.dataset.resumeTemplate;

    const format = (total) => {
        const hours = Math.floor(total / 3_600);
        const minutes = Math.floor((total % 3_600) / 60);
        const seconds = total % 60;
        const clock = `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

        return template.replace('%s', clock);
    };

    const tick = () => {
        const remaining = target - Math.floor(Date.now() / 1_000);

        if (remaining <= 0) {
            clearInterval(resumeCountdownTimer);
            element.textContent = format(0);
            window.location.reload();

            return;
        }

        element.textContent = format(remaining);
    };

    tick();
    resumeCountdownTimer = setInterval(tick, 1_000);
};

export const markBulkActionSubmitting = (form, submitter) => {
    form.querySelectorAll('.bulk-bar button[type=submit]').forEach(button => {
        button.disabled = true;
    });

    submitter.classList.add('submitting');
    rememberTaskGroup(form);
};

const reopenStorageKey = 'tasks-reopen-group';

export const rememberTaskGroup = (element) => {
    const group = element.closest('[data-task-group]');

    if (group) {
        sessionStorage.setItem(reopenStorageKey, group.dataset.groupKey);
    }
};

export const openGroupFromUrl = () => {
    const key = new URLSearchParams(window.location.search).get('open');

    if (!key) {
        return;
    }

    const group = document.querySelector(`[data-group-key="${CSS.escape(key)}"]`);

    if (!group) {
        return;
    }

    group.classList.remove('collapsed');
    refreshExpandAllLabel();
    group.scrollIntoView({block: 'center'});
};

export const reopenRememberedGroup = () => {
    const key = sessionStorage.getItem(reopenStorageKey);

    if (key === null) {
        return;
    }

    sessionStorage.removeItem(reopenStorageKey);

    const group = document.querySelector(`[data-group-key="${key}"]`);

    if (!group) {
        return;
    }

    group.classList.remove('collapsed');
    refreshExpandAllLabel();
};

const resetBulkScope = (checkbox) => {
    const form = checkbox.closest('form');
    const scopeInput = form?.querySelector('[data-bulk-scope]');

    if (!scopeInput || scopeInput.value === '') {
        return;
    }

    scopeInput.value = '';

    const selectAllButton = form.querySelector('[data-bulk-select-all]');
    selectAllButton.classList.remove('active');
    selectAllButton.textContent = selectAllButton.dataset.pageScopedLabel;
};

const revealBulkBar = (checkbox) => {
    const bulkBar = checkbox.closest('[data-task-group]')?.querySelector('.bulk-bar.visible');

    bulkBar?.scrollIntoView({block: 'nearest', behavior: 'smooth'});
};

document.addEventListener('groupedcheckboxchanged', (event) => {
    resetBulkScope(event.checkbox);
    revealBulkBar(event.checkbox);
});

document.addEventListener('confirmed', (event) => {
    const deleteButton = event.target.closest('[data-bulk-delete]');

    if (deleteButton) {
        deleteButton.classList.add('confirming');
        deleteButton.closest('form').requestSubmit(deleteButton);
    }
});
